import {
  FiClock,
  FiBookmark,
  FiFileText,
  FiTruck,
  FiTrendingUp,
  FiPieChart,
  FiShield,
  FiSearch,
  FiBook,
  FiMessageSquare,
  FiTerminal,
  FiMail,
  FiShoppingCart,
  FiTag,
  FiSettings,
  FiBox,
  FiDownload,
  FiUsers,
  FiActivity,
  FiKey,
  FiDollarSign,
} from "react-icons/fi";
import { LuLayoutDashboard } from "react-icons/lu";
import { BiHomeAlt } from "react-icons/bi";
import { LiaStickyNote } from "react-icons/lia";

/**
 * Mapa de presentación (Tarea 10): cómo se ve cada vista — ruta, icono, grupo
 * y etiqueta del menú. Este archivo NO decide quién ve qué: eso lo resuelve
 * puedeVer(clave) de usePermissions() (Tarea 9), a partir de las `vistas`
 * concedidas al usuario. Un typo aquí deja la clave sin efecto (nadie la ve,
 * sin error visible), así que las claves deben copiarse tal cual del catálogo.
 *
 * El orden de las claves define el orden del menú: entradas consecutivas con
 * el mismo `grupo` se agrupan en una sola sección plegable; `grupo: null`
 * se renderiza como enlace suelto, fuera de cualquier sección (hoy ninguna
 * entrada lo usa, pero sigue soportado).
 *
 * `metricas_consultas` es la única vista con comportamiento especial: para
 * super admin, App.jsx sustituye su ruta/componente por /dashboard +
 * DashboardHome en vez de /dashboard-alertas + DashboardAlertas. Ese switch
 * vive en App.jsx porque depende de isSuperAdmin (dato de sesión), no de
 * presentación.
 *
 * Claves del catálogo que a propósito NO están en este mapa, porque no son
 * pantallas del dashboard:
 *
 * - `finanzas_desglose`: pese a estar marcada `tipo: 'vista'` en la tabla
 *   `vistas`, en el frontend no tiene ruta propia — protege los endpoints
 *   del backend `desglose-financiero` y `comisiones-pasarela` (ver
 *   api/rutas.php en legal-check). El desglose financiero que sí se ve en
 *   pantalla vive embebido dentro de DashboardHome (/dashboard), sin gate
 *   propio, y esa página ya está cubierta por `metricas_consultas` +
 *   isSuperAdmin. Mapearla aquí como si tuviera ruta le regalaría el
 *   DashboardHome completo a cualquiera a quien se le conceda la clave
 *   pensando que solo habilita un widget — NO volver a añadirla sin antes
 *   separar el desglose en su propia vista/ruta.
 * - `accion_marcar_devolucion`, `accion_compras_monitoreo_vivo`,
 *   `accion_ver_todas_consultas`, `accion_registrar_recarga_srn`,
 *   `accion_gestionar_descuentos`, `accion_gestionar_planes`,
 *   `accion_editar_parametros_precio`, `accion_reintentar_compra`,
 *   `accion_marcar_compra_completada`, `accion_procesar_ctl_manual`,
 *   `accion_sincronizar_mercado_vehicular`: `tipo: 'accion'` en el catálogo,
 *   no `'vista'` — son botones dentro de una pantalla, no pantallas en sí.
 *   No generan entrada de menú ni ruta; se consumen con puedeVer(clave)
 *   directamente en los componentes (Tarea 11), no aquí.
 *
 * `asignacion_permisos` (Tarea 13) es la única entrada cuya clave de objeto
 * NO es una clave del catálogo del backend: no existe (ni debe inventarse)
 * una vista propia para "pantalla de asignación de permisos" en la tabla
 * `vistas`. En vez de eso, declara `permiso: 'superadmin_panel'` para que
 * App.jsx la gatee con esa clave real — la misma que abre el Panel
 * SuperAdmin — sin que dos entradas del objeto compitan por la misma key de
 * JS. Solo esta entrada usa `permiso`; el resto sigue con clave === permiso.
 */
export const VISTAS = {
  metricas_consultas: {
    ruta: "/dashboard-alertas",
    icono: LuLayoutDashboard,
    grupo: "Dashboard",
    label: "Métricas Consultas",
  },
  // Consumo Clientes era un enlace suelto (`grupo: null`) a media lista, y es
  // la otra cara de las métricas: cuánto se consulta y quién lo consume. Van
  // juntas bajo Dashboard, y por eso esta entrada se movió aquí desde más
  // abajo: el menú agrupa por entradas CONSECUTIVAS con el mismo `grupo`, así
  // que ponerle `grupo: "Dashboard"` sin moverla habría creado una segunda
  // sección "Dashboard" más abajo en vez de sumarla a esta.
  consumo_clientes: {
    ruta: "/gestion-consumo",
    icono: FiPieChart,
    grupo: "Dashboard",
    label: "Consumo Clientes",
  },
  // Predios y Certificados eran dos grupos y son lo mismo: el certificado de
  // tradición y libertad ES el documento de un predio. Separarlos partía en dos
  // menús un único trámite —consultar el predio, ver el histórico, revisar los
  // CTL pendientes— y obligaba a adivinar en cuál de los dos estaba cada cosa.
  //
  // Las tres entradas van SEGUIDAS a propósito: el menú agrupa por entradas
  // consecutivas con el mismo `grupo`, así que separarlas crearía dos secciones
  // plegables con el mismo nombre.
  predios_consultar: {
    ruta: "/reportes-solicitudes",
    icono: BiHomeAlt,
    grupo: "Predios",
    label: "Consultar",
  },
  // Pedir un CTL SIN pasar por la pasarela: cortesía del equipo, o una venta
  // cobrada por fuera. Clave propia y no la de los listados: ver la cola es
  // gratis, pedir un CTL se lo compra al SNR (migración 2026-09-04).
  predio_consulta_interna: {
    ruta: "/predio-consulta-interna",
    icono: FiSearch,
    grupo: "Predios",
    label: "Consulta interna",
  },
  certificados_historico: {
    ruta: "/informes",
    icono: LiaStickyNote,
    grupo: "Predios",
    label: "Historico consultas",
  },
  certificados_ctl_pend: {
    ruta: "/ctl-pendientes",
    icono: FiClock,
    grupo: "Predios",
    label: "CTL pendientes",
  },
  vehicular_pendientes: {
    ruta: "/historico-vehicular-pendientes",
    icono: FiClock,
    grupo: "Histórico Vehicular",
    label: "Pendientes",
  },
  vehicular_mercado: {
    ruta: "/precios-mercado-vehiculos",
    icono: FiTrendingUp,
    grupo: "Histórico Vehicular",
    label: "Estudio de mercado",
  },
  // Encolar un informe SIN PAGO para el usuario de la sesión. Clave propia y no
  // la de pendientes: ver la cola es gratis, encolar gasta cupo del proveedor.
  // Se concede solo a super_admin (migración 2026-08-19).
  vehicular_consulta_interna: {
    ruta: "/historico-vehicular-consulta-interna",
    icono: FiSearch,
    grupo: "Histórico Vehicular",
    label: "Consulta interna",
  },
  personas_nueva: {
    ruta: "/nueva-consulta-antecedentes",
    icono: FiSearch,
    grupo: "Personas/Empresas",
    label: "Nueva Consulta",
  },
  personas_historico: {
    ruta: "/historial-consulta-antecedentes",
    icono: LiaStickyNote,
    grupo: "Personas/Empresas",
    label: "Historico anotaciones",
  },
  entrenamiento_textos: {
    ruta: "/textos",
    icono: FiBook,
    grupo: "Entrenamiento",
    label: "Textos entrenamiento",
  },
  monitoreo_whatsapp: {
    ruta: "/envios-whatsapp-senpulse",
    icono: FiMessageSquare,
    grupo: "Monitoreo",
    label: "Envios Whatsapp sp",
  },
  monitoreo_logs: {
    ruta: "/logger-view",
    icono: FiTerminal,
    grupo: "Monitoreo",
    label: "Logs y Análisis",
  },
  monitoreo_correo: {
    ruta: "/correo-enviado",
    icono: FiMail,
    grupo: "Monitoreo",
    label: "Correo enviado",
  },
  monitoreo_compras_auto: {
    ruta: "/cola-pendiente-compra",
    icono: FiShoppingCart,
    grupo: "Monitoreo",
    label: "Compras automáticas",
  },
  config_descuentos: {
    ruta: "/codigos-descuento",
    icono: FiTag,
    grupo: "Configuración",
    label: "Códigos de descuento",
  },
  config_parametros: {
    ruta: "/parametros",
    icono: FiSettings,
    grupo: "Configuración",
    label: "Parámetros App",
  },
  config_planes: {
    ruta: "/planes-servicios",
    icono: FiBox,
    grupo: "Configuración",
    label: "Planes y Servicios",
  },
  config_informes: {
    ruta: "/informes-descarga",
    icono: FiDownload,
    grupo: "Configuración",
    label: "Descargar informes",
  },
  flujo_caja: {
    ruta: "/flujo-caja",
    icono: FiDollarSign,
    grupo: "Configuración",
    label: "Flujo de Caja",
  },
  superadmin_panel: {
    ruta: "/superadmin",
    icono: FiShield,
    grupo: "Super Admin",
    label: "Panel SuperAdmin",
  },
  rc_pendientes: {
    ruta: "/registro-civil-pendientes",
    icono: FiFileText,
    grupo: "Super Admin",
    label: "RC Pendientes",
  },
  superadmin_anotaciones: {
    ruta: "/anotaciones",
    icono: FiBookmark,
    grupo: "Super Admin",
    label: "Tipos de anotaciones",
  },
  asignacion_permisos: {
    ruta: "/asignacion-permisos",
    icono: FiKey,
    grupo: "Super Admin",
    label: "Asignar permisos",
    permiso: "superadmin_panel",
  },
};

/** Icono de cada grupo (accordion) del menú. El orden real del menú lo marca
 * el orden de las claves en VISTAS, no este mapa. */
export const GRUPO_ICONOS = {
  Dashboard: LuLayoutDashboard,
  Predios: BiHomeAlt,
  "Histórico Vehicular": FiTruck,
  "Personas/Empresas": FiUsers,
  Entrenamiento: FiBook,
  Monitoreo: FiActivity,
  Configuración: FiSettings,
  "Super Admin": FiShield,
};
